"use client";

import { useEffect, useRef, useState } from "react";
import type { Locale } from "@/lib/i18n/config";
import { getDictionary } from "@/lib/i18n/dictionaries";

const FASTRATE_WIDGET_ID = "0e6fc6da-b166-4279-817a-b3050435e191";

declare global {
  interface Window {
    EMRPixel?: { init: (host: string, id: number) => void };
  }
}

export function GoogleReviews({ locale }: { locale: Locale }) {
  const t = getDictionary(locale).googleReviews;
  const sectionRef = useRef<HTMLElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Lazy-load: vent med at hente revw.me-scriptet til sektionen kommer i
  // viewport (eller tæt på). Det sparer ~94 KB transfer + 72ms main-thread
  // + 123ms forced reflow på initial page load.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || shouldLoad) return;

    // Hvis IntersectionObserver mangler (gamle browsere): load efter et delay
    if (typeof IntersectionObserver === "undefined") {
      const t = setTimeout(() => setShouldLoad(true), 3000);
      return () => clearTimeout(t);
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px" } // start load lidt før den scroller ind
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    if (window.EMRPixel) return;
    const script = document.createElement("script");
    script.defer = true;
    script.src = `https://cdn2.revw.me/js/pixel.js?t=${864e5 * Math.ceil(Date.now() / 864e5)}`;
    script.charset = "utf-8";
    script.onload = () => {
      window.EMRPixel?.init("app.fastrate.io", 105);
    };
    document.body.appendChild(script);
  }, [shouldLoad]);

  return (
    <section ref={sectionRef} className="pt-0 pb-12 lg:pb-16">
      <div className="container-wide">
        <div className="text-center max-w-[600px] mx-auto mb-8">
          <span className="eyebrow !mx-auto">{t.eyebrow}</span>
          <h2 className="font-[family-name:var(--font-display-loaded)] text-[28px] lg:text-[36px] font-bold text-[var(--color-navy-700)] mb-3">
            {t.title} <em className="text-[var(--color-primary-700)] not-italic">{t.titleEm}</em>
          </h2>
          <p className="text-[var(--color-muted)] text-[16px] leading-relaxed">{t.lead}</p>
        </div>
        <div className="mt-8 min-h-[280px]">
          {shouldLoad && (
            // @ts-expect-error - custom element fra FastRate
            <emr-simple-carousel widget-id={FASTRATE_WIDGET_ID}></emr-simple-carousel>
          )}
        </div>
      </div>
    </section>
  );
}
